import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { produce } from 'immer';
import { has, size } from 'lodash';

import { CloudCartsApi } from '@/api/domains/cloud-carts.api';
import { CLOUD_CART_ENTRY_SOURCE_MANUAL, type CloudCartEntrySource } from '@/api/domains/cloud-carts.constants';
import { CloudCartsQueryKeys } from '@/api/domains/cloud-carts.query-keys';
import { useGlobalErrorStore } from '@/components/dialogs';
import { useCentralWarehousePharmacyId } from '@/shared/hooks/useFilialName';

import {
  cancelCloudCartQueriesByCartId,
  getBaseCloudCartsQueryOptions,
  getCurrentCloudCartId,
  invalidateCloudCartQueriesByCartId,
  useCurrentCloudCartId,
  useUpdateCloudCart,
  type BaseCloudCartsOptions,
} from './cloud-carts-base';
import {
  computeTotalPackagesCount,
  transformCloudCartData,
  transformFilialCloudCartData,
  updateCloudCartCache,
  type FilialCloudCartData,
} from './cloud-carts.helpers';
import type { TransformedCloudCart } from './cloud-carts.types';

// ----------------------------------------------------------------------

export type UseCloudCartSelector<TData> = (data: TransformedCloudCart) => TData;

export type UseCloudCartOptions<TData = TransformedCloudCart> = BaseCloudCartsOptions & {
  select?: UseCloudCartSelector<TData>;
};

export const useCloudCart = <TData = TransformedCloudCart>(
  options: UseCloudCartOptions<TData>,
): UseQueryResult<TData> =>
  useQuery({
    queryKey: CloudCartsQueryKeys.getCloudCartKey({ cartId: options.cartId }),
    queryFn: async () => {
      // !!! There is no additional check for `cartId` because the function should not return `undefined` !!!
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const rawCloudCart = await CloudCartsApi.fetchCloudCart({ cartId: options.cartId! });

      return transformCloudCartData(rawCloudCart);
    },
    select: options.select,
    ...getBaseCloudCartsQueryOptions(options),
  });

// ----------------------------------------------------------------------

export type UseCurrentCloudCartOptions<TData> = Omit<UseCloudCartOptions<TData>, 'cartId'>;

export const useCurrentCloudCart = <TData = TransformedCloudCart>(options?: UseCurrentCloudCartOptions<TData>) =>
  useCloudCart({
    cartId: useCurrentCloudCartId(),
    ...options,
  });

// ----------------------------------------------------------------------

export type UseUpdateCloudCartEntryOptions = {
  drugId: number;
  offerId: number;
  entrySource?: CloudCartEntrySource;
};

export const useUpdateCloudCartEntry = ({
  drugId,
  offerId,
  entrySource = CLOUD_CART_ENTRY_SOURCE_MANUAL,
}: UseUpdateCloudCartEntryOptions) => {
  const queryClient = useQueryClient();

  const { debouncedMutate, setUpdateStatus } = useUpdateCloudCart();

  const updateCloudCartEntry = async (count: number) => {
    setUpdateStatus(true);

    const cartId = await getCurrentCloudCartId(queryClient);
    await cancelCloudCartQueriesByCartId(queryClient, cartId);

    queryClient.setQueryData<TransformedCloudCart>(CloudCartsQueryKeys.getCloudCartKey({ cartId }), prevData => {
      if (!prevData) {
        return;
      }

      return produce(prevData, data => {
        const { entriesPackagesCounts } = updateCloudCartCache({
          data,
          drugId,
          offerId,
          count,
        });

        data.rawCloudCart.items_count = size(entriesPackagesCounts);
        data.rawCloudCart.packages_count = computeTotalPackagesCount(entriesPackagesCounts);
      });
    });

    return debouncedMutate({
      cartId,
      payload: {
        offer_id: offerId,
        quantity: count,
        source: entrySource,
      },
    });
  };

  return {
    updateCloudCartEntry,
  };
};

// ----------------------------------------------------------------------
// Грид «Заказ по филиалам»: чтение и запись пофилиальных количеств одного оффера.
// Источник истины — корзина (scoped-запрос по offer_ids=[offerId]).

export type UseFilialCloudCartEntriesOptions = {
  enabled?: boolean;
};

export const useFilialCloudCartEntries = (offerId?: number, options?: UseFilialCloudCartEntriesOptions) => {
  const cartId = useCurrentCloudCartId();

  const { data } = useQuery({
    // -1 — недостижимый offer_id для disabled-состояния, чтобы ключ не совпал с общей корзиной.
    queryKey: CloudCartsQueryKeys.getCloudCartKey({ cartId, offer_ids: [offerId ?? -1] }),
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const rawCloudCart = await CloudCartsApi.fetchCloudCart({ cartId: cartId!, offer_ids: [offerId!] });

      return transformFilialCloudCartData(rawCloudCart);
    },
    enabled: !!cartId && !!offerId && options?.enabled !== false,
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const getFilialQuantity = useCallback((pharmacyId: number) => data?.pharmacyQuantities[pharmacyId] ?? 0, [data]);

  return {
    getFilialQuantity,
    offerTotal: data?.offerTotal ?? 0,
  };
};

// ----------------------------------------------------------------------
// Строка «Центральный склад» грида «Заказ по филиалам»: суммарное заказанное количество
// препарата по всем офферам. Источник истины — корзина (scoped-запрос по drug_ids=[drugId]).

export const useDrugCloudCartTotal = (drugId?: number) => {
  const cartId = useCurrentCloudCartId();

  const { data } = useQuery({
    // -1 — недостижимый drug_id для disabled-состояния, чтобы ключ не совпал с общей корзиной.
    queryKey: CloudCartsQueryKeys.getCloudCartKey({ cartId, drug_ids: [drugId ?? -1] }),
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const rawCloudCart = await CloudCartsApi.fetchCloudCart({ cartId: cartId!, drug_ids: [drugId!] });

      return transformFilialCloudCartData(rawCloudCart);
    },
    enabled: !!cartId && !!drugId,
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Заказанное количество препарата в разрезе аптеки (агрегат по всем офферам). Центральный
  // склад читается по pharmacy_id = contractor_id. Не зависит от выбранного оффера — грид
  // «Заказ по филиалам» показывает итоги, стабильные при смене строки в верхнем гриде.
  const getPharmacyQuantity = useCallback((pharmacyId: number) => data?.pharmacyQuantities[pharmacyId] ?? 0, [data]);

  return {
    total: data?.offerTotal ?? 0,
    getPharmacyQuantity,
  };
};

// ----------------------------------------------------------------------
// Оптимистичное обновление количества одной аптеки оффера: scoped-кэш по офферу, агрегированный
// по препарату (для нижнего грида) и главная корзина (верхний счётчик читает её при OFF).

type ApplyOfferPharmacyOptimisticUpdateParams = {
  queryClient: QueryClient;
  cartId: number;
  offerId: number;
  drugId?: number;
  // Ключ аптеки в кэше: pharmacy_id филиала или centralWarehousePharmacyId для ЦС.
  pharmacyKey: number;
  safeCount: number;
  prevOfferCount: number;
};

const applyOfferPharmacyOptimisticUpdate = ({
  queryClient,
  cartId,
  offerId,
  drugId,
  pharmacyKey,
  safeCount,
  prevOfferCount,
}: ApplyOfferPharmacyOptimisticUpdateParams) => {
  const delta = safeCount - prevOfferCount;

  // scoped-запрос грида филиалов (без offer-level кэша, который сплющил бы пофилиальные строки).
  queryClient.setQueryData<FilialCloudCartData>(
    CloudCartsQueryKeys.getCloudCartKey({ cartId, offer_ids: [offerId] }),
    prevData => {
      if (!prevData) {
        return;
      }

      return produce(prevData, draft => {
        draft.pharmacyQuantities[pharmacyKey] = safeCount;
        draft.offerTotal += delta;
      });
    },
  );

  // Агрегированный по препарату кэш (источник отображения строк нижнего грида).
  if (drugId != null) {
    queryClient.setQueryData<FilialCloudCartData>(
      CloudCartsQueryKeys.getCloudCartKey({ cartId, drug_ids: [drugId] }),
      prevData => {
        if (!prevData) {
          return;
        }

        return produce(prevData, draft => {
          draft.pharmacyQuantities[pharmacyKey] = (draft.pharmacyQuantities[pharmacyKey] ?? 0) + delta;
          draft.offerTotal += delta;
        });
      },
    );
  }

  // Главный кэш корзины: верхний счётчик предложения читает его при выключенном тумблере.
  queryClient.setQueryData<TransformedCloudCart>(CloudCartsQueryKeys.getCloudCartKey({ cartId }), prevData => {
    if (!prevData) {
      return;
    }

    return produce(prevData, data => {
      if (!has(data.entriesPackagesCounts, offerId)) {
        data.entriesPackagesCounts[offerId] = { packagesCount: 0, giftPackagesCount: 0 };
      }
      data.entriesPackagesCounts[offerId].packagesCount += delta;

      if (drugId != null) {
        if (!has(data.drugsPackagesCount, drugId)) {
          data.drugsPackagesCount[drugId] = { packagesCount: 0 };
        }
        data.drugsPackagesCount[drugId].packagesCount += delta;
      }

      data.rawCloudCart.items_count = size(data.entriesPackagesCounts);
      data.rawCloudCart.packages_count = computeTotalPackagesCount(data.entriesPackagesCounts);
    });
  });
};

// ----------------------------------------------------------------------

export type UseUpdateFilialCloudCartEntryOptions = {
  offerId: number;
  // undefined — центральный склад: заказ пишется без pharmacy_id.
  pharmacyId?: number;
  // Задан — дополнительно оптимистично обновляем агрегированный по препарату кэш
  // (источник отображения грида «Заказ по филиалам»).
  drugId?: number;
  entrySource?: CloudCartEntrySource;
};

export const useUpdateFilialCloudCartEntry = ({
  offerId,
  pharmacyId,
  drugId,
  entrySource = CLOUD_CART_ENTRY_SOURCE_MANUAL,
}: UseUpdateFilialCloudCartEntryOptions) => {
  const queryClient = useQueryClient();
  const centralWarehousePharmacyId = useCentralWarehousePharmacyId();

  const { debouncedMutate, setUpdateStatus } = useUpdateCloudCart();

  const updateFilialCloudCartEntry = async (count: number) => {
    setUpdateStatus(true);

    const cartId = await getCurrentCloudCartId(queryClient);
    await cancelCloudCartQueriesByCartId(queryClient, cartId);

    const safeCount = Math.max(0, count);
    const pharmacyKey = pharmacyId ?? centralWarehousePharmacyId;

    // Прежнее количество ВЫБРАННОГО оффера по этой аптеке — база дельты для агрегированного
    // по препарату кэша (в нём количества аптеки суммированы по всем офферам).
    const offerScopedData = queryClient.getQueryData<FilialCloudCartData>(
      CloudCartsQueryKeys.getCloudCartKey({ cartId, offer_ids: [offerId] }),
    );
    const prevOfferCount = offerScopedData?.pharmacyQuantities[pharmacyKey] ?? 0;

    applyOfferPharmacyOptimisticUpdate({
      queryClient,
      cartId,
      offerId,
      drugId,
      pharmacyKey,
      safeCount,
      prevOfferCount,
    });

    return debouncedMutate({
      cartId,
      payload: {
        offer_id: offerId,
        quantity: safeCount,
        source: entrySource,
        pharmacy_id: pharmacyId,
      },
    });
  };

  return {
    updateFilialCloudCartEntry,
  };
};

// ----------------------------------------------------------------------
// Верхний счётчик предложения с распределением по филиалам: задаём итог оффера, распределяя
// изменение по аптекам — прибавка идёт на центральный склад, убавка снимается сначала с ЦС,
// затем с филиалов по переданному порядку (orderedFilialIds — как в нижнем гриде, по имени).
// Мутации немедленные (не debounced): за один вызов могут меняться разные аптеки, и debounce
// схлопнул бы их в одну. Итог берётся из свежего кэша, поэтому частые клики не рассыпаются.

export type UseOfferTotalDistributionOptions = {
  offerId: number;
  drugId?: number;
  entrySource?: CloudCartEntrySource;
};

export const useOfferTotalDistribution = ({
  offerId,
  drugId,
  entrySource = CLOUD_CART_ENTRY_SOURCE_MANUAL,
}: UseOfferTotalDistributionOptions) => {
  const queryClient = useQueryClient();
  const centralWarehousePharmacyId = useCentralWarehousePharmacyId();

  const { updateCloudCartItemMutation, setUpdateStatus } = useUpdateCloudCart();

  const setPharmacyQuantity = (cartId: number, pharmacyId: number | undefined, count: number) => {
    const pharmacyKey = pharmacyId ?? centralWarehousePharmacyId;
    const scopedData = queryClient.getQueryData<FilialCloudCartData>(
      CloudCartsQueryKeys.getCloudCartKey({ cartId, offer_ids: [offerId] }),
    );
    const prevOfferCount = scopedData?.pharmacyQuantities[pharmacyKey] ?? 0;

    applyOfferPharmacyOptimisticUpdate({
      queryClient,
      cartId,
      offerId,
      drugId,
      pharmacyKey,
      safeCount: count,
      prevOfferCount,
    });

    updateCloudCartItemMutation.mutate({
      cartId,
      payload: { offer_id: offerId, quantity: count, source: entrySource, pharmacy_id: pharmacyId },
    });
  };

  const distribute = (cartId: number, target: number, orderedFilialIds: number[]) => {
    const scopedData = queryClient.getQueryData<FilialCloudCartData>(
      CloudCartsQueryKeys.getCloudCartKey({ cartId, offer_ids: [offerId] }),
    );
    const quantities = scopedData?.pharmacyQuantities ?? {};
    const currentTotal = scopedData?.offerTotal ?? 0;
    const currentCentral = quantities[centralWarehousePharmacyId] ?? 0;
    const safeTarget = Math.max(0, target);

    // Прибавка — на центральный склад.
    if (safeTarget >= currentTotal) {
      setPharmacyQuantity(cartId, undefined, currentCentral + (safeTarget - currentTotal));

      return;
    }

    // Убавка — сначала с ЦС, потом с филиалов по порядку (setPharmacyQuantity читает свежий кэш,
    // поэтому последовательные вызовы корректно накапливают offerTotal).
    let toRemove = currentTotal - safeTarget;

    const centralRemove = Math.min(toRemove, currentCentral);
    if (centralRemove > 0) {
      setPharmacyQuantity(cartId, undefined, currentCentral - centralRemove);
      toRemove -= centralRemove;
    }

    for (const filialId of orderedFilialIds) {
      if (toRemove <= 0) {
        break;
      }

      const quantity = quantities[filialId] ?? 0;
      if (quantity <= 0) {
        continue;
      }

      const remove = Math.min(toRemove, quantity);
      setPharmacyQuantity(cartId, filialId, quantity - remove);
      toRemove -= remove;
    }
  };

  const prepare = async () => {
    setUpdateStatus(true);

    const cartId = await getCurrentCloudCartId(queryClient);
    await cancelCloudCartQueriesByCartId(queryClient, cartId);

    return cartId;
  };

  // Относительное изменение (кнопки +/- и стрелки): итог берётся из свежего кэша.
  const changeOfferTotalBy = async (delta: number, orderedFilialIds: number[]) => {
    const cartId = await prepare();
    const currentTotal =
      queryClient.getQueryData<FilialCloudCartData>(
        CloudCartsQueryKeys.getCloudCartKey({ cartId, offer_ids: [offerId] }),
      )?.offerTotal ?? 0;

    distribute(cartId, currentTotal + delta, orderedFilialIds);
  };

  // Абсолютное значение (ручной ввод).
  const setOfferTotal = async (target: number, orderedFilialIds: number[]) => {
    const cartId = await prepare();

    distribute(cartId, target, orderedFilialIds);
  };

  return {
    changeOfferTotalBy,
    setOfferTotal,
  };
};

// ----------------------------------------------------------------------
// Удаление одной пофилиальной строки. Строка корзины = один сырой CloudCartItem (offer_id +
// pharmacy_id), поэтому её cart_item_id берём из кэша по паре (offer_id, pharmacy_id) и удаляем
// точечно через by-ids (иначе quantity: 0 оставлял бы строку в корзине с нулевым количеством).

export type UseDeleteFilialCloudCartEntryOptions = {
  offerId: number;
  pharmacyId: number;
};

export const useDeleteFilialCloudCartEntry = ({ offerId, pharmacyId }: UseDeleteFilialCloudCartEntryOptions) => {
  const queryClient = useQueryClient();
  const { setGlobalError } = useGlobalErrorStore();
  const centralWarehousePharmacyId = useCentralWarehousePharmacyId();

  const isReady = centralWarehousePharmacyId !== 0;

  const deleteFilialMutation = useMutation({
    mutationFn: CloudCartsApi.deleteCloudCartEntriesByIds,
    onSettled: (_, __, { cartId }) => invalidateCloudCartQueriesByCartId(queryClient, cartId),
    onError: setGlobalError,
  });

  const deleteFilialCloudCartEntry = async () => {
    const cartId = await getCurrentCloudCartId(queryClient);

    const rawItems =
      queryClient.getQueryData<TransformedCloudCart>(CloudCartsQueryKeys.getCloudCartKey({ cartId }))?.rawCloudCart
        .items ?? [];

    const cartItemIds = rawItems
      .filter(item => item.offer_id === offerId && item.pharmacy_id === pharmacyId)
      .map(item => item.id);

    if (cartItemIds.length === 0) {
      return;
    }

    return deleteFilialMutation.mutate({
      cartId,
      payload: {
        cart_item_ids: cartItemIds,
      },
    });
  };

  return {
    isReady,
    isLoading: deleteFilialMutation.isLoading,
    deleteFilialCloudCartEntry,
  };
};

// ----------------------------------------------------------------------
// Есть ли у оффера пофилиальные (не ЦС) строки с количеством > 0. Если да — верхний счётчик
// предложения переводится в read-only (правка ведётся через нижний грид «Заказ по филиалам»),
// иначе обычный заказ (без pharmacy_id, т.е. на ЦС) складывался бы с пофилиальными строками.
export const useOfferHasFilialQuantities = (offerId?: number) => {
  const centralWarehousePharmacyId = useCentralWarehousePharmacyId();

  const { data = false } = useCurrentCloudCart({
    select: useCallback(
      (cloudCart: TransformedCloudCart) => {
        if (!offerId) {
          return false;
        }

        return cloudCart.rawCloudCart.items.some(
          item => item.offer_id === offerId && item.pharmacy_id !== centralWarehousePharmacyId && item.quantity > 0,
        );
      },
      [offerId, centralWarehousePharmacyId],
    ),
  });

  return data;
};
